import axios from 'axios';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from './locales/en';
import sq from './locales/sq';
import de from './locales/de';
export const LANGUAGES = [
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'sq', label: 'Shqip',   flag: '🇦🇱' },
    { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
] as const;

// Safari throws on localStorage access in Private Browsing and when storage is
// blocked, and every read here used to be unguarded — one throw took out the
// whole language lookup and silently dropped the page back to English.
export function readStoredLanguage(): string | null {
    const code = readStored('fade_lang_device');
    return code && LANGUAGES.some(l => l.code === code) ? code : null;
}

function readStored(key: string): string | null {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
}

function writeStored(key: string, value: string) {
    try {
        localStorage.setItem(key, value);
    } catch {
        /* Private Browsing / storage disabled — in-memory language still applies. */
    }
}

function getInitialLanguage(userId?: number): string {
    // Always check device language first (works for both admin and booking pages)
    const deviceLang = readStored('fade_lang_device');
    if (deviceLang && LANGUAGES.some(l => l.code === deviceLang)) {
        return deviceLang;
    }

    // Check for user-specific language (for admin users)
    if (userId) {
        const savedLang = readStored(`fade_lang_${userId}`);
        if (savedLang && LANGUAGES.some(l => l.code === savedLang)) {
            return savedLang;
        }
    }

    // Legacy booking language key
    const bookingLang = readStored('fade_booking_lang');
    if (bookingLang && LANGUAGES.some(l => l.code === bookingLang)) {
        return bookingLang;
    }

    // Nothing stored on this device: fall back to the language on the signed-in
    // user's account. iOS gives a Home Screen web app its own storage partition,
    // so a language picked in the standalone app leaves Safari's copy empty —
    // without this the dashboard booted English there while the installed app
    // showed Albanian. The account language is the same in both.
    const accountLang = readInertiaUserLanguage();
    if (accountLang) return accountLang;

    return LANGUAGES[0].code;
}

/**
 * The signed-in user's language, read from the Inertia payload that is already
 * in the DOM when this module runs.
 *
 * i18n.init() happens at import time, long before React mounts and any effect
 * can consult `auth.user.language`, so without reading it here the first render
 * is committed in the fallback locale.
 */
function readInertiaUserLanguage(): string | null {
    if (typeof document === 'undefined') return null;

    try {
        const payload = document.getElementById('app')?.dataset.page;
        if (!payload) return null;

        const code = JSON.parse(payload)?.props?.auth?.user?.language;
        return typeof code === 'string' && LANGUAGES.some(l => l.code === code) ? code : null;
    } catch {
        return null;
    }
}

/**
 * The one place a language choice is persisted.
 *
 * The marketing nav used to write only `fade_lang_device` while the dashboard
 * switcher wrote the device key, the per-user key and the database. Picking
 * Albanian on the home page therefore left the server on 'en', so any later
 * load that fell through to the database — a new device, or Safari having
 * evicted localStorage — came back English while the rest of the app stayed
 * Albanian. Both switchers go through here now so the two can't drift.
 */
export function persistLanguage(code: string, userId?: number) {
    if (!LANGUAGES.some(l => l.code === code)) return;

    i18n.changeLanguage(code);

    writeStored('fade_lang_device', code);
    writeStored('fade_booking_lang', code);
    if (userId) writeStored(`fade_lang_${userId}`, code);
}

// Mirror the active language to the server so it can localize the strings it
// generates (validation errors, mails). The header covers XHR/Inertia visits;
// the cookie covers full page loads, where no JS has run yet.
function syncServerLocale(lang: string) {
    if (typeof document === 'undefined') return;

    // Inertia visits go through this same axios instance, so the header rides
    // along on form submissions too.
    axios.defaults.headers.common['X-App-Locale'] = lang;
    document.cookie = `fade_lang=${lang};path=/;max-age=31536000;samesite=lax`;
}

i18n
    .use(initReactI18next)
    .init({
        resources: {
            en: { translation: en },
            sq: { translation: sq },
            de: { translation: de },
        },
        lng: getInitialLanguage(),
        fallbackLng: 'en',
        supportedLngs: ['en', 'sq', 'de'],
        interpolation: { escapeValue: false },
        initImmediate: false,
    });

syncServerLocale(i18n.language);
i18n.on('languageChanged', syncServerLocale);

// Update language if userId changes (e.g., after login/logout)
export function initializeUserLanguage(userId?: number) {
    const initialLang = getInitialLanguage(userId);
    if (i18n.language !== initialLang) {
        i18n.changeLanguage(initialLang);
    }
}

// Persist the booking page language for refresh (shop owner's selected language)
export function setBookingLanguage(lang: string) {
    if (LANGUAGES.some(l => l.code === lang)) {
        localStorage.setItem('fade_booking_lang', lang);
        i18n.changeLanguage(lang);
    }
}

export default i18n;
